import { App } from '@capacitor/app';
import { registerPlugin } from '@capacitor/core';
import { getDeviceUuid } from 'app/services/secureTokenStore';

import { logger } from 'utils/logger';
import { getIsNativePlatform, getNativePlatform } from 'utils/platform';

import { CAPACITOR_UPDATER_PLUGIN_NAME, DEVICE_PLUGIN_NAME } from './deviceTelemetry.constants';

const devicePlugin = registerPlugin(DEVICE_PLUGIN_NAME);
const capacitorUpdaterPlugin = registerPlugin(CAPACITOR_UPDATER_PLUGIN_NAME);

// The @capacitor/app, Device, and CapacitorUpdater plugins are native-only; on web they throw
// "not implemented on web". That is the expected web outcome, not a failure — so short-circuit to
// null there instead of catching-and-logging, which otherwise spams the console on every page that
// renders AppVersionInline (the footer/menu build tag). Genuine native plugin failures still log.
const readAppInfo = () => {
  if (!getIsNativePlatform()) {
    return Promise.resolve(null);
  }

  return App.getInfo().catch(error => {
    logger.error('[DeviceTelemetry] failed to read app info', error);

    return null;
  });
};

const readDeviceInfo = () => {
  if (!getIsNativePlatform()) {
    return Promise.resolve(null);
  }

  return devicePlugin.getInfo().catch(error => {
    logger.error('[DeviceTelemetry] failed to read device info', error);

    return null;
  });
};

const readCurrentBundle = () => {
  if (!getIsNativePlatform()) {
    return Promise.resolve(null);
  }

  return capacitorUpdaterPlugin.current().catch(error => {
    logger.error('[DeviceTelemetry] failed to read current bundle', error);

    return null;
  });
};

// The builtin bundle (a fresh install, before any OTA sync) reports a null channel and version
// from current(); getChannel() returns the device's resolved channel (the native defaultChannel)
// even then. Falls back to null on error so the caller's coalescing can take over.
const readChannel = () => {
  if (!getIsNativePlatform()) {
    return Promise.resolve(null);
  }

  return capacitorUpdaterPlugin
    .getChannel()
    .then(result => result?.channel ?? null)
    .catch(() => null);
};

// channel + bundleVersion are REQUIRED, non-blank server-side (RecordMobileOtaPingCommandHandler
// 400s on a blank/null Channel or BundleVersion). On the builtin bundle both come back null from
// current(), which silently dropped every fresh-install ping. Fall channel back to the live
// getChannel(), then the build-time `window.lsOtaChannel` (the env channel build-web inlined per
// bundle — dev/stage/production), and a 'builtin' sentinel last; bundleVersion to the native app
// version — so a device records on first open, before it has ever pulled an OTA bundle.
//
// In our self-hosted Capgo both current().channel and getChannel() come back null, so without
// the inlined lsOtaChannel the ping fell to 'builtin' even on a dev build (DEV-3923).
const BUILTIN = 'builtin';

export const getBuildInfo = async () => {
  const [appInfo, deviceInfo, currentBundle, resolvedChannel] = await Promise.all([
    readAppInfo(),
    readDeviceInfo(),
    readCurrentBundle(),
    readChannel(),
  ]);

  return {
    bundleVersion: currentBundle?.bundle?.version ?? appInfo?.version ?? BUILTIN,
    bundleId: appInfo?.id ?? null,
    appNativeVersion: appInfo?.version ?? null,
    versionBuild: appInfo?.build ?? null,
    channel: currentBundle?.channel ?? resolvedChannel ?? globalThis.lsOtaChannel ?? BUILTIN,
    platform: getNativePlatform(),
    isEmulator: deviceInfo?.isVirtual ?? false,
    model: deviceInfo?.model ?? null,
    osVersion: deviceInfo?.osVersion ?? null,
    deviceUuid: getDeviceUuid(),
  };
};

export const checkForBundleUpdate = async () => {
  if (!getIsNativePlatform()) {
    return { found: false };
  }

  try {
    const current = await readCurrentBundle();
    const latest = await capacitorUpdaterPlugin.getLatest();
    const currentVersion = current?.bundle?.version ?? null;
    const latestVersion = latest?.version ?? null;

    return {
      version: latestVersion,
      found: !!(latestVersion && latestVersion !== currentVersion),
    };
  } catch (error) {
    logger.error('[DeviceTelemetry] failed to check for bundle update', error);

    return { error: true };
  }
};

export const getTelemetryContext = async () => {
  const [appInfo, deviceInfo, currentBundle, resolvedChannel] = await Promise.all([
    readAppInfo(),
    readDeviceInfo(),
    readCurrentBundle(),
    readChannel(),
  ]);

  const nativeVersion = appInfo?.version ?? null;

  return {
    deviceUuid: getDeviceUuid(),
    appId: appInfo?.id ?? null,
    platform: getNativePlatform(),
    osVersion: deviceInfo?.osVersion ?? null,
    model: deviceInfo?.model ?? null,
    manufacturer: deviceInfo?.manufacturer ?? null,
    isEmulator: deviceInfo?.isVirtual ?? false,
    locale: globalThis.navigator?.language ?? null,
    appNativeVersion: nativeVersion,
    versionBuild: appInfo?.build ?? null,
    bundleVersion: currentBundle?.bundle?.version ?? nativeVersion ?? BUILTIN,
    channel: currentBundle?.channel ?? resolvedChannel ?? globalThis.lsOtaChannel ?? BUILTIN,
  };
};
